import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Phone, Mail, MessageCircle, MessageSquare, Send, CheckCircle, Clock, ArrowRight, AlertTriangle, CalendarClock, Flame, Thermometer, Snowflake } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  formatDateTime, formatRelativeTime, isOverdue,
  PRIORITY_CONFIG,
} from '../../utils/nurtureUtils';

const FollowUpCard = ({ followUp, onComplete, onSelectLead }) => {
  const overdue = isOverdue(followUp.scheduled_at);
  const lead = followUp.lead || {};
  const PriorityIcon = lead.priority === 'Hot' ? Flame : lead.priority === 'Warm' ? Thermometer : Snowflake;
  const priorityClass =
    lead.priority === 'Hot' ? 'text-rose-500 dark:text-rose-400' :
    lead.priority === 'Warm' ? 'text-amber-500 dark:text-amber-400' :
    'text-blue-500 dark:text-blue-400';

  // Strip phone of spaces for tel:/sms: link (keeps + for international)
  const phoneClean = lead.phone ? lead.phone.replace(/\s+/g, '') : '';
  const telHref = phoneClean ? `tel:${phoneClean}` : null;
  const smsHref = phoneClean ? `sms:${phoneClean}` : null;
  // WhatsApp wants digits only in international format. AU default: '0' → '61'.
  const phoneDigits = phoneClean ? phoneClean.replace(/[^\d+]/g, '') : '';
  let waDigits = phoneDigits.replace(/^\+/, '');
  if (waDigits.startsWith('0')) waDigits = `61${waDigits.slice(1)}`;
  const waHref = waDigits ? `https://wa.me/${waDigits}` : null;
  const mailHref = lead.email ? `mailto:${lead.email}` : null;

  return (
    <Card
      className={cn(
        'border-l-4 transition-shadow hover:shadow-md dark:bg-slate-900 dark:border-slate-800',
        overdue
          ? 'border-l-red-500 bg-red-50/70 dark:bg-rose-950/30 dark:border-l-rose-500'
          : 'border-l-blue-500 dark:border-l-blue-500'
      )}
      data-testid={`followup-card-${followUp.id}`}
    >
      <CardContent className="p-3 sm:p-4">
        {/* Top row: name + priority + Done button */}
        <div className="flex items-start justify-between gap-3 mb-2">
          <button
            type="button"
            onClick={() => onSelectLead && onSelectLead(lead)}
            className="text-left min-w-0 flex-1 group focus:outline-none"
            data-testid={`followup-name-${followUp.id}`}
          >
            <div className="flex items-center gap-1.5 mb-0.5">
              <PriorityIcon className={cn('w-4 h-4 flex-shrink-0', priorityClass)} />
              <p className="font-semibold text-slate-900 dark:text-white truncate group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                {lead.name || 'Unknown lead'}
              </p>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
              {lead.lead_type || ''}{lead.lead_source ? ` • ${lead.lead_source}` : ''}
            </p>
          </button>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <Badge variant="outline" className="text-xs dark:border-slate-700 dark:text-slate-300 hidden sm:inline-flex">
              {followUp.method}
            </Badge>
            <Button
              size="sm"
              onClick={() => onComplete(followUp)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid={`followup-done-${followUp.id}`}
            >
              <CheckCircle className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Done</span>
            </Button>
          </div>
        </div>

        {/* Contact row: phone + sms + whatsapp + email - clickable */}
        <div className="flex flex-wrap items-center gap-2 mt-2 text-sm">
          {telHref ? (
            <a
              href={telHref}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 transition-colors font-medium border border-emerald-200/60 dark:border-emerald-500/30"
              data-testid={`followup-call-${followUp.id}`}
              onClick={(e) => e.stopPropagation()}
              aria-label={`Call ${lead.phone}`}
            >
              <Phone className="w-3.5 h-3.5" />
              <span className="text-xs sm:text-sm">{lead.phone}</span>
            </a>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs text-slate-400 dark:text-slate-500">
              <Phone className="w-3.5 h-3.5" /> No phone
            </span>
          )}
          {smsHref && (
            <a
              href={smsHref}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-500/20 transition-colors font-medium border border-violet-200/60 dark:border-violet-500/30"
              data-testid={`followup-sms-${followUp.id}`}
              onClick={(e) => e.stopPropagation()}
              aria-label="Send SMS"
              title="Send SMS"
            >
              <MessageSquare className="w-3.5 h-3.5" />
              <span className="text-xs sm:text-sm">SMS</span>
            </a>
          )}
          {waHref && (
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-500/20 transition-colors font-medium border border-green-200/60 dark:border-green-500/30"
              data-testid={`followup-whatsapp-${followUp.id}`}
              onClick={(e) => e.stopPropagation()}
              aria-label="Open WhatsApp chat"
              title="Open WhatsApp"
            >
              <MessageCircle className="w-3.5 h-3.5" />
              <span className="text-xs sm:text-sm">WhatsApp</span>
            </a>
          )}
          {mailHref ? (
            <a
              href={mailHref}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-500/20 transition-colors font-medium border border-blue-200/60 dark:border-blue-500/30 truncate max-w-[220px]"
              data-testid={`followup-email-${followUp.id}`}
              onClick={(e) => e.stopPropagation()}
              aria-label={`Email ${lead.email}`}
            >
              <Mail className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="text-xs sm:text-sm truncate">{lead.email}</span>
            </a>
          ) : null}
        </div>

        {/* Schedule row */}
        <div className="flex items-center justify-between gap-2 mt-3 pt-2 border-t border-slate-100 dark:border-slate-800">
          <div
            className={cn(
              'inline-flex items-center gap-1.5 text-xs sm:text-sm',
              overdue
                ? 'text-rose-600 dark:text-rose-400 font-semibold'
                : 'text-slate-600 dark:text-slate-300'
            )}
          >
            {overdue ? <AlertTriangle className="w-4 h-4" /> : <CalendarClock className="w-4 h-4 text-slate-400 dark:text-slate-500" />}
            <span>
              {overdue ? 'OVERDUE: ' : ''}
              {formatDateTime(followUp.scheduled_at)}
            </span>
          </div>
          <span className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500 sm:hidden">
            {followUp.method}
          </span>
        </div>

        {followUp.notes && (
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 italic line-clamp-2">
            "{followUp.notes}"
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export { FollowUpCard };
export default FollowUpCard;
